import { useEffect, useState, useMemo } from "react";
import client from "@/lib/api";
import { prefetchInvite, getInviteCache } from "@/lib/invitePrefetch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShareNetwork, ChatCircle, EnvelopeSimple, LinkSimple, PaperPlaneTilt, Copy, X, XLogo, FacebookLogo, InstagramLogo, SnapchatLogo, Check } from "@phosphor-icons/react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;
// Parse a pasted block (commas, spaces, semicolons, newlines) → deduped recipient chips.
function parseRecipients(str) {
  const seen = new Set(); const out = [];
  for (const raw of (str || "").split(/[\s,;]+/)) {
    const t = raw.trim().toLowerCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push({ email: t, valid: EMAIL_RE.test(t) });
  }
  return out;
}

// One button → native share sheet → the link does the rest (per build spec).
export function InviteDialog({ open, onClose }) {
  const { user } = useAuth();
  const [inv, setInv] = useState(getInviteCache());
  const [fallback, setFallback] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [emailVal, setEmailVal] = useState("");
  const [sending, setSending] = useState(false);
  const [storyGuide, setStoryGuide] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFallback(false); setEmailMode(false); setEmailVal("");
    // Prefetched at app load — resolves instantly if already cached.
    prefetchInvite().then((d) => { if (d) setInv(d); }).catch(() => {});
  }, [open]);

  // Build the invite link SYNCHRONOUSLY from the code we already have on the user
  // (returned by /auth/me), so Share / Copy / X / Facebook enable instantly without
  // waiting on the /me/invite round-trip. The fetched payload (exact share text +
  // versioned card image) swaps in when it arrives.
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const synthetic = useMemo(() => {
    const code = user?.invite_code;
    if (!code) return null;
    const url = `${BACKEND}/api/i/${code}`;
    return {
      code,
      url,
      human_url: `${BACKEND}/i/${code}`,
      image_url: `${BACKEND}/api/i/${code}/card.png`,
      share_text: `Stop Scrolling. Start Streaming.\nJoin my friend circle on Screening Circle.\n${url}`,
    };
  }, [user?.invite_code, BACKEND]);
  const eff = inv || synthetic;

  async function shareInvite() {
    if (!eff) return;
    // Same-origin card URL so the fetched blob is readable (eff.image_url is on the
    // backend host = cross-origin and its blob can't be read for share/clipboard).
    const cardUrl = eff?.code
      ? `${process.env.REACT_APP_BACKEND_URL}/api/i/${eff.code}/card.png`
      : eff.image_url;
    // Best case: native sheet with the graphic attached (mobile).
    try {
      const blob = await (await fetch(cardUrl, { mode: "cors" })).blob();
      const file = new File([blob], "screening-circle.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: eff.share_text });
        return;
      }
    } catch (_) { /* fall through */ }
    // Next: native sheet, text + link only (the OG card renders the graphic).
    if (navigator.share) {
      try { await navigator.share({ text: eff.share_text, url: eff.url }); return; }
      catch (e) { if (e.name === "AbortError") return; }
    }
    // Desktop / Firefox: reveal three inline actions (no modal wizard).
    setFallback(true);
  }

  const copyLink = () => navigator.clipboard.writeText(eff?.url || eff?.human_url || "")
    .then(() => toast.success("Link copied")).catch(() => toast.error("Couldn't copy"));

  // Copy the invite so pasting into a rich app (Gmail / iMessage) drops BOTH the
  // message text AND the card graphic together. The trick: rich editors pick a single
  // clipboard flavor, and only `text/html` can carry text + image at once — so we build
  // an HTML fragment (text + link + the card embedded as an inline base64 <img>) and
  // pair it with `text/plain` for plain fields. Falls back to text-only.
  const copyInvite = async () => {
    const text = eff?.share_text || eff?.url || "";
    if (!text) return;
    const link = eff?.url || eff?.human_url || "";
    // Fetch the card from the SAME origin the app runs on (eff.image_url points at the
    // backend's APP_URL host, which is cross-origin → its blob can't be read for the
    // clipboard). Using REACT_APP_BACKEND_URL keeps the fetch same-origin.
    const cardUrl = eff?.code
      ? `${process.env.REACT_APP_BACKEND_URL}/api/i/${eff.code}/card.png`
      : eff?.image_url;
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    try {
      if (cardUrl && window.ClipboardItem && navigator.clipboard?.write) {
        const blob = await fetch(cardUrl, { mode: "cors" }).then((r) => {
          if (!r.ok) throw new Error("card fetch failed");
          return r.blob();
        });
        // blob → base64 data URI so the image travels inside the HTML fragment itself.
        const dataUri = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        // Body text with the URL turned into a clickable link; card image below it.
        let bodyHtml = esc(text);
        if (link) bodyHtml = bodyHtml.split(esc(link)).join(`<a href="${esc(link)}">${esc(link)}</a>`);
        bodyHtml = bodyHtml.replace(/\n/g, "<br>");
        const html = `<div>${bodyHtml}<br><br><img src="${dataUri}" alt="Screening Circle invite" style="max-width:100%;border-radius:12px;"></div>`;
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
        toast.success("Copied! Image + text ready to paste");
        return;
      }
    } catch { /* fall through to text-only */ }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied! Paste it into any text or email");
    } catch { toast.error("Couldn't copy — try again"); }
  };

  // --- Social share (same actions as the Share-a-title dialog) ---
  // Use eff.url (the crawlable /api/i/{code} OG page → personalized invite card),
  // NOT human_url (/i/{code}) which hits the SPA and serves the generic OG image.
  const crawlUrl = eff?.url || eff?.human_url || "";
  const withSrc = (u, p) => (u ? u + (u.includes("?") ? "&" : "?") + "src=" + p : u);
  // Caption without the URL (the X/FB intents append the link themselves).
  const caption = (eff?.share_text || "").split(eff?.url || "\u0000").join("").split(eff?.human_url || "\u0000").join("").trim();

  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(withSrc(crawlUrl, "x"))}`, "_blank", "noopener");
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withSrc(crawlUrl, "facebook"))}`, "_blank", "noopener");
  // Instagram / Snapchat have no web post-intent — hand the user the invite image
  // (native share sheet on mobile → post to Stories; download on desktop) + copy caption.
  const shareToStoryApp = async (platform) => {
    const label = platform === "instagram" ? "Instagram" : "Snapchat";
    const cardUrl = eff?.code ? `${process.env.REACT_APP_BACKEND_URL}/api/i/${eff.code}/card.png?format=story` : eff?.image_url;
    if (!cardUrl) { toast.error("Invite image isn't ready yet — try again in a moment"); return; }
    try { await navigator.clipboard.writeText(eff?.share_text || crawlUrl); } catch { /* ignore */ }
    try {
      const blob = await (await fetch(cardUrl, { mode: "cors" })).blob();
      const file = new File([blob], `screening-circle-${platform}.png`, { type: "image/png" });
      const isTouch = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
      if (isTouch && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: eff?.share_text });
        toast.success("Caption copied — pick your Story to post");
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setStoryGuide({ label, appUrl: platform === "instagram" ? "https://www.instagram.com/" : "https://www.snapchat.com/" });
    } catch { toast.error("Couldn't prepare the invite image — try again"); }
  };
  const shareInstagram = () => shareToStoryApp("instagram");
  const shareSnapchat = () => shareToStoryApp("snapchat");

  const SOCIAL = [
    { key: "x", label: "X", Icon: XLogo, color: "#000000", onClick: shareX },
    { key: "facebook", label: "Facebook", Icon: FacebookLogo, color: "#1877F2", onClick: shareFacebook },
    { key: "instagram", label: "Instagram", Icon: InstagramLogo, color: "#E1306C", onClick: shareInstagram },
    { key: "snapchat", label: "Snapchat", Icon: SnapchatLogo, color: "#FFFC00", dark: true, onClick: shareSnapchat },
  ];

  const sendEmail = async () => {
    const valid = parseRecipients(emailVal).filter((c) => c.valid).map((c) => c.email).slice(0, MAX_RECIPIENTS);
    if (valid.length === 0) { toast.error("Enter a valid email"); return; }
    try {
      await client.post("/invites/send", { channel: "email", contact: valid.join(",") });
      toast.success(valid.length > 1 ? `${valid.length} invites sent` : "Invite sent");
      setEmailVal(""); setEmailMode(false);
    } catch { toast.error("Couldn't send — try again"); }
    finally { setSending(false); }
  };

  const removeRecipient = (email) => {
    const rest = parseRecipients(emailVal).filter((c) => c.email !== email).map((c) => c.email);
    setEmailVal(rest.join(", "));
  };

  const recipients = parseRecipients(emailVal);
  const validCount = recipients.filter((c) => c.valid).length;

  const shareText = eff?.share_text || "";
  // QR encodes the crawlable /api/i/{code} link so a scanned/pasted invite also
  // previews the personalized card on Facebook/X (human_url hits the SPA → generic OG).
  const qrUrl = eff?.url || eff?.human_url || "";
  const qr = qrUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(qrUrl)}` : "";

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden border-white/10 bg-elevated p-0 text-white sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader className="shrink-0 px-5 pb-1 pt-5">
          <DialogTitle className="font-display text-2xl font-semibold">Invite a friend</DialogTitle>
          <DialogDescription className="text-dim">Send your invite through any app — it lands them on a page that connects you automatically.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 pt-3">
          {/* Preview card */}
          <div>
            <div className="overflow-hidden rounded-[14px] border border-white/10 bg-black">
              {eff?.image_url
                ? <img src={eff.image_url} alt="Your invite preview" data-testid="invite-preview-card" className="block w-full object-cover" />
                : <div className="aspect-[1200/630] w-full animate-pulse bg-white/[0.04]" />}
            </div>
            <p className="mt-1.5 text-center text-[11.5px] text-faint">This is what your friends will see</p>
          </div>

          {/* What happens when they open it — gold panel to draw attention */}
          <div className="rounded-[12px] bg-gold p-4 shadow-[0_8px_24px_rgba(212,175,55,0.25)]" data-testid="invite-how-it-works">
            <p className="text-[12.5px] leading-relaxed text-gold-ink/80">
              <span className="font-bold text-gold-ink">One link to share with anyone.</span> When a friend opens it:
            </p>
            <ul className="mt-1.5 space-y-1 text-[12.5px] leading-relaxed text-gold-ink/80">
              <li>• <span className="font-semibold text-gold-ink">New to Screening Circle?</span> They'll be invited to join and you'll be connected automatically.</li>
              <li>• <span className="font-semibold text-gold-ink">Already a member?</span> They'll get a request to become friends with you.</li>
            </ul>
          </div>

          {/* Actions: neutral by default, gold on hover */}
          <Button onClick={shareInvite} disabled={!eff} variant="outline" data-testid="invite-share-btn"
            className="w-full rounded-full border-white/15 bg-transparent py-6 text-[16px] font-bold text-[#F6F5F1] transition-colors hover:border-gold hover:bg-gold hover:text-gold-ink">
            <ShareNetwork size={19} weight="fill" className="mr-2" /> Share invite
          </Button>

          <Button onClick={copyInvite} disabled={!eff} variant="outline" data-testid="invite-copy-btn"
            className="w-full rounded-full border-white/15 bg-transparent py-6 text-[16px] font-bold text-[#F6F5F1] transition-colors hover:border-gold hover:bg-gold hover:text-gold-ink">
            <Copy size={19} weight="fill" className="mr-2" /> Copy invite
          </Button>

          {/* Social row — same actions as Share a title */}
          <div className="grid grid-cols-4 gap-2" data-testid="invite-social-row">
            {SOCIAL.map(({ key, label, Icon, color, dark, onClick }) => (
              <button key={key} onClick={onClick} disabled={!eff} data-testid={`invite-${key}`}
                className="flex flex-col items-center gap-1.5 rounded-[14px] border border-white/[0.08] bg-surface py-3 transition-colors hover:border-gold/40 disabled:opacity-40">
                <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: color }}>
                  <Icon size={22} weight="fill" color={dark ? "#000000" : "#ffffff"} />
                </span>
                <span className="text-[11px] font-semibold text-dim">{label}</span>
              </button>
            ))}
          </div>

          {/* Desktop / Firefox fallback row (inline, not a modal) */}
          {fallback && (
            <div className="grid grid-cols-3 gap-2" data-testid="invite-fallback-row">
              <a href={`sms:?&body=${encodeURIComponent(shareText)}`} data-testid="invite-fallback-sms"
                className="flex flex-col items-center gap-1 rounded-[12px] border border-white/12 bg-black/30 py-3 text-xs text-[#F6F5F1] hover:border-gold">
                <ChatCircle size={18} className="text-gold" /> Text
              </a>
              <a href={`mailto:?subject=${encodeURIComponent("Join me on Screening Circle")}&body=${encodeURIComponent(shareText)}`} data-testid="invite-fallback-email"
                className="flex flex-col items-center gap-1 rounded-[12px] border border-white/12 bg-black/30 py-3 text-xs text-[#F6F5F1] hover:border-gold">
                <EnvelopeSimple size={18} className="text-gold" /> Email
              </a>
              <button onClick={copyLink} data-testid="invite-fallback-copy"
                className="flex flex-col items-center gap-1 rounded-[12px] border border-white/12 bg-black/30 py-3 text-xs text-[#F6F5F1] hover:border-gold">
                <LinkSimple size={18} className="text-gold" /> Copy link
              </button>
            </div>
          )}

          {/* Email it for me (server sends the designed HTML email) */}
          {!emailMode ? (
            <Button onClick={() => setEmailMode(true)} disabled={!eff} variant="outline" data-testid="invite-email-mode-toggle"
              className="w-full rounded-full border-white/15 bg-transparent py-6 text-[16px] font-bold text-[#F6F5F1] transition-colors hover:border-gold hover:bg-gold hover:text-gold-ink">
              <PaperPlaneTilt size={19} className="mr-2" /> Email it for me
            </Button>
          ) : (
            <div className="rounded-[12px] border border-white/10 bg-surface p-3" data-testid="invite-email-panel">
              <p className="mb-2 text-[12.5px] text-dim">We'll send each of them a designed invite email from Screening Circle. <span className="text-faint">Paste or type as many emails as you like — separate them with commas, spaces, or new lines.</span></p>
              <Textarea value={emailVal} onChange={(e) => setEmailVal(e.target.value)} rows={3} placeholder={"friend@email.com, another@email.com\nthird@email.com"} data-testid="invite-email-input"
                className="resize-none border-white/10 bg-black/30 text-white placeholder:text-faint focus-visible:ring-gold" />

              {/* Recipient chips preview */}
              {recipients.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="invite-recipient-chips">
                  {recipients.map((c) => (
                    <span key={c.email} data-testid={`invite-chip-${c.valid ? "valid" : "invalid"}`}
                      className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] ${c.valid ? "border-gold/40 bg-gold/10 text-[#F6F5F1]" : "border-red-500/40 bg-red-500/10 text-red-300"}`}
                      title={c.valid ? c.email : `${c.email} — not a valid email`}>
                      <span className="truncate">{c.email}</span>
                      <button onClick={() => removeRecipient(c.email)} data-testid="invite-chip-remove" aria-label={`Remove ${c.email}`}
                        className="shrink-0 rounded-full opacity-70 transition-opacity hover:opacity-100">
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-[11.5px] text-faint" data-testid="invite-recipient-count">
                  {validCount === 0 ? "No valid emails yet"
                    : `${validCount} recipient${validCount > 1 ? "s" : ""} ready`}
                  {validCount > MAX_RECIPIENTS ? ` · only the first ${MAX_RECIPIENTS} will be sent` : ""}
                </p>
                <Button onClick={sendEmail} disabled={sending || validCount === 0} data-testid="invite-email-send" variant="outline" className="shrink-0 rounded-full border-white/15 bg-transparent px-5 font-bold text-[#F6F5F1] transition-colors hover:border-gold hover:bg-gold hover:text-gold-ink disabled:opacity-40">{sending ? "Sending…" : validCount > 1 ? `Send ${Math.min(validCount, MAX_RECIPIENTS)}` : "Send"}</Button>
              </div>
            </div>
          )}

          {/* QR for face-to-face sharing — at the bottom */}
          {qr && (
            <div className="flex items-center gap-4 rounded-[14px] border border-white/10 bg-surface p-4" data-testid="invite-qr">
              <img src={qr} alt="Invite QR code" className="h-[88px] w-[88px] shrink-0 rounded-lg bg-white p-1.5" />
              <div className="min-w-0">
                <p className="text-[14px] font-bold text-[#F6F5F1]">Sharing in person?</p>
                <p className="mt-0.5 text-[12.5px] leading-snug text-dim">Have your friend point their camera at this code to join your friend group on Screening Circle.</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={!!storyGuide} onOpenChange={(v) => !v && setStoryGuide(null)}>
      <DialogContent className="border-white/10 bg-elevated text-white sm:max-w-sm" data-testid="invite-story-saved-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-semibold">
            <Check size={22} weight="bold" className="text-gold" /> Invite image saved
          </DialogTitle>
          <DialogDescription className="text-dim">
            Saved to your downloads and your caption is copied — {storyGuide?.label} can’t receive it directly from a computer, so add it in a few taps:
          </DialogDescription>
        </DialogHeader>
        <ol className="my-1 space-y-2 text-[13.5px] text-[#F6F5F1]">
          <li><span className="mr-2 font-bold text-gold">1.</span>Open {storyGuide?.label} (or send the image to your phone)</li>
          <li><span className="mr-2 font-bold text-gold">2.</span>Start a new Story and add the saved image</li>
          <li><span className="mr-2 font-bold text-gold">3.</span>Paste your caption — it’s already copied</li>
        </ol>
        <div className="mt-1 flex gap-2">
          <Button variant="outline" className="flex-1 border-white/15" onClick={() => setStoryGuide(null)} data-testid="invite-story-saved-close">Done</Button>
          <Button className="flex-1 bg-gold text-gold-ink hover:bg-gold/90"
            onClick={() => { window.open(storyGuide?.appUrl, "_blank", "noopener"); setStoryGuide(null); }}
            data-testid="invite-story-saved-open-app">Open {storyGuide?.label}</Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
